import React, { useState } from 'react';
import { Row, Col, Typography, Spin, Card, Modal, Table } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { Pie } from 'react-chartjs-2';
import HorizontalBar from './HorizontalBar';
import './DataRecordedTab.css';

const { Title, Text } = Typography;

const cardStyle = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const cardBodyStyle = {
  flexGrow: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  minHeight: '300px',
};

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff8042',
  '#00C49F',
  '#FFBB28',
];

const getPieChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    position: 'bottom',
  },
  tooltips: {
    callbacks: {
      label(tooltipItem, data) {
        const dataset = data.datasets[tooltipItem.datasetIndex];
        const total = dataset.data.reduce((acc, value) => acc + value, 0);
        const currentValue = dataset.data[tooltipItem.index];
        const percentage = total > 0 ? ((currentValue / total) * 100).toFixed(1) : 0;
        return ` ${data.labels[tooltipItem.index]}: ${currentValue} (${percentage}%)`;
      },
    },
  },
  plugins: {
    datalabels: {
      formatter: (value, ctx) => {
        const total = ctx.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
        const percentage = total > 0 ? (value / total) * 100 : 0;
        if (percentage > 5) {
          return `${percentage.toFixed(0)}%`;
        }
        return null;
      },
      color: '#fff',
      font: {
        weight: 'bold',
      },
    },
  },
});


const DataRecordedTab = ({ sessionData }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [modalData, setModalData] = useState({ title: '', content: [], type: '' });

  const showModal = (title, data, type = 'general') => {
    setModalData({ title, content: data, type });
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
  };

  const getTableColumns = (type) => {
    switch (type) {
      case 'api':
        return [
          { title: 'API Name', dataIndex: 'label', key: 'label' },
          { title: 'Category', key: 'category', render: () => 'API Category' },
          { title: 'Frequency', dataIndex: 'value', key: 'value', sorter: (a, b) => a.value - b.value, sortDirections: ['descend', 'ascend'] },
          { title: 'Notes', key: 'notes', render: (_, record) => `Used ${record.value} times during this session` },
        ];
      case 'language':
        return [
          { title: 'Language Name', dataIndex: 'label', key: 'label' },
          { title: 'Category', key: 'category', render: () => 'Language' },
          // { title: 'Frequency', dataIndex: 'value', key: 'value', sorter: (a, b) => a.value - b.value, sortDirections: ['descend', 'ascend'] },
          { title: 'Notes', key: 'notes', render: (_, record) => `Detected ${record.value}% in conversation` },
        ];
      default:
        return [
          { title: 'Name', dataIndex: 'label', key: 'label' },
          { title: 'Category', key: 'category', render: () => 'General Category' },
          { title: 'Frequency', dataIndex: 'value', key: 'value', sorter: (a, b) => a.value - b.value, sortDirections: ['descend', 'ascend'] },
          { title: 'Notes', key: 'notes', render: (_, record) => `Occurred ${record.value} times` },
        ];
    }
  };

  const prepareTableData = (data) => data.map((item, index) => ({ key: index, ...item }));

  if (!sessionData) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const detectedLanguages = sessionData.languageUsed?.detected_languages || {};
  const languageDataForChart = {
    labels: Object.keys(detectedLanguages),
    datasets: [{
      data: Object.values(detectedLanguages),
      backgroundColor: COLORS,
      borderColor: '#fff',
      borderWidth: 1,
    }],
  };
  const languageDataForModal = Object.entries(detectedLanguages).map(([lang, count]) => ({ label: lang, value: count }));

  const unansweredData = sessionData.unansweredQuestions || { count: 0, list: [] };
  const totalQuestionsAsked = (sessionData.questions?.length || 0) + (sessionData.nonQuestions?.length || 0);
  const answeredCount = totalQuestionsAsked > unansweredData.count ? totalQuestionsAsked - unansweredData.count : 0;
  const questionChartData = {
    labels: ['Answered', "Can't Answer"],
    datasets: [{
      data: [answeredCount, unansweredData.count],
      backgroundColor: COLORS,
      borderColor: '#fff',
      borderWidth: 1,
    }],
  };
  const questionDataForModal = [{ label: 'Answered', value: answeredCount }, { label: "Can't Answer", value: unansweredData.count }];

  const apiCallsMap = sessionData.externalApi || new Map();
  const apiCallEntries = apiCallsMap instanceof Map ? Array.from(apiCallsMap.entries()) : Object.entries(apiCallsMap);
  const filteredApiCalls = apiCallEntries
    .filter(([, data]) => data && data.count > 0)
    .map(([name, data]) => ({ label: name, value: data.count }));

  const poiFrequencies = (sessionData.poiList || []).reduce((acc, poi) => {
    const name = poi.name || 'Unknown POI';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const poiData = Object.entries(poiFrequencies).map(([name, count]) => ({ label: name, value: count }));

  const mediaFrequencies = (sessionData.mediaList || []).reduce((acc, media) => {
    const name = typeof media === 'string' ? media.split('/').pop() : media.name || 'Unknown Media';
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});
  const mediaData = Object.entries(mediaFrequencies).map(([name, count]) => ({ label: name, value: count }));

  return (
    <div style={{ padding: '16px', background: '#f0f2f5' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title="Language(s) used" bordered={false} style={cardStyle} extra={<span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} onClick={() => showModal('Languages Used', languageDataForModal, 'language')}>This session {languageDataForModal.length} <RightOutlined /></span>}>
            <div style={cardBodyStyle}>
              {languageDataForChart.labels.length > 0 ? (
                <Pie data={languageDataForChart} options={getPieChartOptions()} />
              ) : (
                <Text type="secondary">No language data</Text>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="External API used" bordered={false} style={cardStyle} extra={<span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} onClick={() => showModal('External APIs Used', filteredApiCalls, 'api')}>This session {filteredApiCalls.length} <RightOutlined /></span>}>
            <div style={cardBodyStyle}>
              <HorizontalBar data={filteredApiCalls} color="#8884d8" />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Question Answering" bordered={false} style={cardStyle} extra={<span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} onClick={() => showModal('Question Answering', questionDataForModal)}>This session {totalQuestionsAsked} <RightOutlined /></span>}>
            <div style={cardBodyStyle}>
              {totalQuestionsAsked > 0 ? (
                <Pie data={questionChartData} options={getPieChartOptions()} />
              ) : (
                <Text type="secondary">No questions asked</Text>
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="POI Card(s) Recorded" bordered={false} style={cardStyle} extra={<span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} onClick={() => showModal('POI Cards Recorded', poiData)}>This session {poiData.length} <RightOutlined /></span>}>
            <div style={cardBodyStyle}>
              <HorizontalBar data={poiData} color="#82ca9d" />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Media Card(s) Recorded" bordered={false} style={cardStyle} extra={<span style={{ fontSize: '12px', color: '#666', cursor: 'pointer' }} onClick={() => showModal('Media Cards Recorded', mediaData)}>This session {mediaData.length} <RightOutlined /></span>}>
            <div style={cardBodyStyle}>
              <HorizontalBar data={mediaData} color="#ffc658" />
            </div>
          </Card>
        </Col>
      </Row>

      <Modal open={modalVisible} onCancel={handleModalClose} footer={null} width={900} bodyStyle={{ padding: 0 }} className="title-modal" title={<div><Title level={4} style={{ margin: 0 }}>Details</Title><Text type="secondary" style={{ fontSize: '12px' }}>{modalData.title}</Text></div>}>
        <Table columns={getTableColumns(modalData.type)} dataSource={prepareTableData(modalData.content)} pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`, size: 'small' }} size="small" scroll={{ y: 400 }} />
      </Modal>
    </div>
  );
};

export default DataRecordedTab;